// Browser-side file download helpers (SC-3149) — the first place this app has
// ever handed the user a file. Kept apart from `client.ts` because none of it is
// HTTP: it turns bytes the client already fetched into a saved file.

// Characters no mainstream OS accepts in a filename, plus the C0/DEL controls.
// eslint-disable-next-line no-control-regex -- matching control chars is the point
const UNSAFE_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f\u007f]+/g;
const FILENAME_MAX_CHARS = 120;

// How long the object URL outlives the click. Revoking synchronously after
// `click()` works in Chromium but can cancel the download in Firefox/Safari,
// which resolve the blob URL asynchronously; a short grace period is the
// standard fix. After it the blob is released — a 25K-row file pinned in memory
// for the life of the tab is not free.
export const REVOKE_DELAY_MS = 10_000;

/**
 * Reduce a server- or user-supplied name to something safe to hand to
 * `<a download>`: no path components, no reserved or control characters, no
 * leading dots (hidden files), bounded length. Returns `''` when nothing
 * survives, so the caller can fall back.
 */
export function sanitizeFilename(raw: string): string {
  // Last path segment only — a header naming `../../x.csv` names `x.csv`.
  const base = raw.split(/[/\\]/).pop() ?? '';
  const cleaned = base
    .replace(UNSAFE_FILENAME_CHARS, '-')
    .replace(/\s+/g, ' ')
    .replace(/^[\s.-]+|[\s.]+$/g, '');
  if (cleaned.length <= FILENAME_MAX_CHARS) return cleaned;
  // Cap from the stem so a long name keeps its extension.
  const dot = cleaned.lastIndexOf('.');
  const ext = dot > 0 && cleaned.length - dot <= 10 ? cleaned.slice(dot) : '';
  return cleaned.slice(0, FILENAME_MAX_CHARS - ext.length).replace(/[\s.-]+$/, '') + ext;
}

/**
 * The filename a `Content-Disposition` header names, or `null` when it names
 * none. Prefers RFC 6266's `filename*=UTF-8''…` over the plain `filename=`,
 * as browsers do. The result is NOT sanitised — pass it through
 * {@link sanitizeFilename} before it names anything.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[2].trim().replace(/^"|"$/g, ''));
    } catch (e) {
      // Malformed percent-encoding — fall through to the plain parameter.
      if (!(e instanceof URIError)) throw e;
    }
  }
  const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]+))/i.exec(header);
  if (!plain) return null;
  const value = plain[1] !== undefined ? plain[1].replace(/\\(.)/g, '$1') : plain[2].trim();
  return value || null;
}

/** `YYYYMMDD` in local time — the day the user exported it, as they'd name it. */
function dateStamp(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
}

/**
 * The name to save an export under: the server's, sanitised, when it sent a
 * usable one; otherwise `<catalog name>-<date>.<ext>`. Either way the extension
 * matches the format asked for — a spreadsheet dispatches on it, and an xlsx
 * saved as `.csv` opens as mojibake.
 */
export function exportFilename(
  header: string | null,
  catalogName: string,
  extension: string,
  now: Date = new Date(),
): string {
  const ext = `.${extension}`;
  const raw = filenameFromContentDisposition(header) ?? '';
  const fromHeader = sanitizeFilename(raw);
  // A bare `.csv` names no file — it sanitises to `csv`, not to nothing.
  if (fromHeader && raw.trim().toLowerCase() !== ext) {
    if (fromHeader.toLowerCase().endsWith(ext)) return fromHeader;
    // Swap a mismatched export extension rather than stacking a second one.
    return `${fromHeader.replace(/\.(csv|xlsx)$/i, '')}${ext}`;
  }
  const stem = sanitizeFilename(catalogName).replace(/\s+/g, '-') || 'catalog';
  return sanitizeFilename(`${stem}-${dateStamp(now)}${ext}`);
}

/**
 * Save `blob` as `filename` via a synthesised `<a download>` click, then revoke
 * the object URL after {@link REVOKE_DELAY_MS}.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    try {
      a.click();
    } finally {
      a.remove();
    }
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}
